import React from 'react'

export default function LegalDisclaimer() {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <div className="legal-header">
          <h1>Legal Disclaimer</h1>
          <div className="last-updated">Last updated: December 2024</div>
        </div>
        
        <div className="disclaimer-content">
          <div className="disclaimer-section">
            <h2>⚕️ Medical Disclaimer</h2>
            <div className="disclaimer-text">
              <p>
                <strong>Important Medical Notice:</strong> The information provided on this platform 
                is for educational and informational purposes only and is not intended as medical advice.
              </p>
              
              <div className="warning-box">
                <h3>🚨 Critical Warning</h3>
                <ul>
                  <li>This platform does NOT provide medical advice</li>
                  <li>Information shared by users is not professionally verified</li>
                  <li>Individual experiences with kratom may vary significantly</li>
                  <li>Kratom may interact with medications or health conditions</li>
                </ul>
              </div>
              
              <h3>Medical Consultation Required</h3>
              <p>
                You should ALWAYS consult with a qualified healthcare professional before:
              </p>
              <ul>
                <li>Starting any kratom regimen</li>
                <li>Changing your current kratom usage</li>
                <li>Using kratom if you have medical conditions</li>
                <li>Combining kratom with medications</li>
                <li>Using kratom during pregnancy or breastfeeding</li>
              </ul>
            </div>
          </div>

          <div className="disclaimer-section">
            <h2>⚖️ Legal Status Disclaimer</h2>
            <div className="disclaimer-text">
              <p>
                <strong>Jurisdiction Responsibility:</strong> Kratom legality varies significantly 
                by federal, state, and local jurisdictions.
              </p>
              
              <div className="legal-status-grid">
                <div className="status-item">
                  <h3>🏛️ Federal Level</h3>
                  <p>Currently legal under federal law but subject to potential DEA scheduling</p>
                </div>
                
                <div className="status-item">
                  <h3>🗺️ State Level</h3>
                  <p>Some states have banned or regulated kratom - check your state laws</p>
                </div>
                
                <div className="status-item">
                  <h3>🏘️ Local Level</h3>
                  <p>Cities and counties may have additional restrictions</p>
                </div>
                
                <div className="status-item">
                  <h3>🌍 International</h3>
                  <p>Many countries prohibit kratom - verify before traveling</p>
                </div>
              </div>
              
              <div className="user-responsibility">
                <h3>Your Legal Responsibility</h3>
                <ul>
                  <li>Research and understand laws in your specific location</li>
                  <li>Stay updated on changing legal status</li>
                  <li>Comply with all applicable laws and regulations</li>
                  <li>Understand that legal status can change without notice</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="disclaimer-section">
            <h2>🏪 Vendor and Product Disclaimer</h2>
            <div className="disclaimer-text">
              <p>
                <strong>Independent Vendor Operations:</strong> Vendors listed on this platform 
                operate independently and are not affiliated with or endorsed by Unbiased Kratom.
              </p>
              
              <h3>Platform Role</h3>
              <ul>
                <li>We provide a platform for vendor discovery and reviews</li>
                <li>We verify business documentation but do not endorse products</li>
                <li>We are not responsible for vendor business practices</li>
                <li>We do not guarantee product quality, safety, or efficacy</li>
              </ul>
              
              <h3>User Due Diligence</h3>
              <p>Users should independently verify:</p>
              <ul>
                <li>Vendor legitimacy and business practices</li>
                <li>Product quality and testing results</li>
                <li>Return and refund policies</li>
                <li>Customer service responsiveness</li>
              </ul>
              
              <h3>Transaction Responsibility</h3>
              <ul>
                <li>All transactions occur directly between users and vendors</li>
                <li>Platform is not party to vendor-customer agreements</li>
                <li>Disputes should be resolved directly with vendors</li>
                <li>Platform cannot guarantee transaction outcomes</li>
              </ul>
            </div>
          </div>

          <div className="disclaimer-section">
            <h2>📊 Information Accuracy Disclaimer</h2>
            <div className="disclaimer-text">
              <p>
                <strong>User-Generated Content:</strong> Much of the information on this platform 
                comes from user reviews, discussions, and vendor submissions.
              </p>
              
              <h3>Information Limitations</h3>
              <ul>
                <li>Content reflects individual user experiences and opinions</li>
                <li>Information may be outdated, incomplete, or inaccurate</li>
                <li>Platform cannot verify all user-submitted information</li>
                <li>Scientific research on kratom is limited and evolving</li>
              </ul>
              
              <h3>Verification Recommendations</h3>
              <ul>
                <li>Cross-reference information from multiple sources</li>
                <li>Consult scientific literature and healthcare professionals</li>
                <li>Verify product claims with independent testing</li>
                <li>Consider information recency and source credibility</li>
              </ul>
            </div>
          </div>

          <div className="disclaimer-section">
            <h2>🛡️ Platform Liability Limitations</h2>
            <div className="disclaimer-text">
              <p>
                <strong>Service Provision:</strong> This platform is provided "as is" without 
                warranties or guarantees of any kind.
              </p>
              
              <div className="liability-categories">
                <div className="liability-item">
                  <h3>Service Availability</h3>
                  <ul>
                    <li>No guarantee of continuous platform availability</li>
                    <li>Technical issues may cause temporary disruptions</li>
                    <li>Maintenance may require planned downtime</li>
                  </ul>
                </div>
                
                <div className="liability-item">
                  <h3>Content Accuracy</h3>
                  <ul>
                    <li>No warranty regarding information accuracy</li>
                    <li>User-generated content is not professionally verified</li>
                    <li>Information may be subjective or outdated</li>
                  </ul>
                </div>
                
                <div className="liability-item">
                  <h3>User Interactions</h3>
                  <ul>
                    <li>Platform not liable for user-to-user interactions</li>
                    <li>Users interact at their own risk and discretion</li>
                    <li>Vendor-customer disputes are outside platform scope</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="disclaimer-section">
            <h2>🔞 Age and Eligibility Requirements</h2>
            <div className="disclaimer-text">
              <h3>Minimum Age Requirement</h3>
              <ul>
                <li>Users must be at least 21 years of age</li>
                <li>Age verification is required during registration</li>
                <li>Falsifying age information results in immediate termination</li>
              </ul>
              
              <h3>Legal Capacity</h3>
              <ul>
                <li>Users must have legal capacity to enter contracts</li>
                <li>Compliance with local laws is user responsibility</li>
                <li>Platform access may be restricted in certain jurisdictions</li>
              </ul>
            </div>
          </div>

          <div className="disclaimer-section">
            <h2>📞 Contact and Reporting</h2>
            <div className="disclaimer-text">
              <h3>Legal Questions</h3>
              <p>
                For legal compliance questions or to report legal concerns, 
                contact: <a href="mailto:legal@unbiasedkratom.com">legal@unbiasedkratom.com</a>
              </p>
              
              <h3>Medical Emergencies</h3>
              <p>
                <strong>Emergency Notice:</strong> If you experience adverse effects from kratom, 
                seek immediate medical attention. Call emergency services (911) for urgent situations.
              </p>
              
              <h3>Reporting Violations</h3>
              <p>
                Report suspected illegal activities, medical emergencies, or platform violations 
                through our reporting system or contact: 
                <a href="mailto:safety@unbiasedkratom.com">safety@unbiasedkratom.com</a>
              </p>
            </div>
          </div>

          <div className="acknowledgment-section">
            <h2>✍️ User Acknowledgment</h2>
            <div className="acknowledgment-text">
              <p>
                By using this platform, you acknowledge that you have read, understood, 
                and agree to be bound by this legal disclaimer along with our Terms of 
                Service and Privacy Policy.
              </p>
              
              <p>
                <strong>Final Reminder:</strong> This platform is for educational purposes only. 
                Users are solely responsible for their decisions and compliance with applicable laws.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}